import Button from "@/common/elements/Button";
import Checkbox from "@/common/elements/InputElements/CheckBox";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import { getOptionsFromRefList, hasCheckSecurity } from "@/util/common_function";
import {
  StyledFlex,
  StyledGridCol,
  StyledGridRow,
  StyledSectionHeading,
} from "@/common/styles";
import { forwardRef, useEffect, useImperativeHandle } from "react";
import { Form, Popconfirm } from "antd";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import SecureFormItem from "@/common/SecureFormItem";
import { labels } from "@/util/label";
import { Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import { SUB_MODULES_LIST_WITH_ID, MODULES_LIST_WITH_ID } from "@/util/common_variable";
import { useSelector } from "react-redux";

// NOTE: No <FieldSecurityProvider> needed here — this component is always
// rendered inside <Property> which already provides entity="InmateProperty".
// SecureFormItem reads the context automatically.

const PropertyListQuickAddForm = forwardRef(
  ({ onQuickAdd, bookingsFieldsEnabled, startingPoint }, ref) => {
    const [form] = Form.useForm();
    const fullSearch = useSelector((s) => s.fullSearch);

    // Sub-module check for Q-Add button (not field-level)
    const canAdd = hasCheckSecurity(
      Security_Types.functionality,
      Entity_types.subModule,
      LABELS_CAPITAL.ADD,
      MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
      SUB_MODULES_LIST_WITH_ID.Inmate_Property
    );

    useImperativeHandle(ref, () => ({
      getFormValues:  () => form.getFieldsValue(),
      // Legacy parity (BookingsInterface.vbs HandleIgnoreFieldDuringQuickAddClear):
      // Loc. 1 / Loc. 2 survive a Q-Add so the next item defaults to the same locker.
      resetForm:      () => {
        const loc2 = form.getFieldValue(["inventoryItems", 0, "storageLocation2Id"]);
        const loc3 = form.getFieldValue(["inventoryItems", 0, "storageLocation3Id"]);
        form.resetFields();
        form.setFieldValue(["inventoryItems", 0, "storageLocation2Id"], loc2);
        form.setFieldValue(["inventoryItems", 0, "storageLocation3Id"], loc3);
      },
      validateFields: () => form.validateFields(),
    }));

    useEffect(() => {
      form.setFieldValue(["inventoryItems", 0, "storageLocation1Id"], 50500006);
      form.setFieldValue(["inventoryItems", 0, "itemStatus"], "Checked In");
    });

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------
    return (
      <>
        <StyledSectionHeading>
          <span>Property List Quick Add</span>
        </StyledSectionHeading>

        <Form form={form} disabled={!bookingsFieldsEnabled}>
          <StyledGridRow gutter={16}>

            {/* Hidden fields — no security needed */}
            <StyledGridCol lg={6}>
              <FormItem name={["inventoryItems", 0, "storageLocation1Id"]} label={labels.context} hidden>
                <input type="hidden" />
              </FormItem>
              <FormItem name={["inventoryItems", 0, "itemStatus"]} hidden>
                <input type="hidden" />
              </FormItem>

              {/* Loc. 1 */}
              <SecureFormItem
                fieldName="storageLocation2Id"
                formItemProps={{
                  name: ["inventoryItems", 0, "storageLocation2Id"],
                  label: "Loc. 1",
                  rules: !fullSearch ? [{ required: true, message: "Loc. 1 is required" }] : [],
                }}
              >
                <Select
                  showSearch
                  options={getOptionsFromRefList("Inmate_Property_Storage_Location_1", false, false, true)}
                />
              </SecureFormItem>
            </StyledGridCol>

            {/* Suggest button — action button, not a secured field */}
            <StyledGridCol lg={3}>
              <FormItem label={<></>}>
                <Popconfirm
                  title="Suggestion"
                  description="Please, pick the loc 1 for the item?"
                  onConfirm={() => {}}
                  okText="Ok"
                  cancelText={null}
                >
                  <Button type="default" color="default" disabled={!bookingsFieldsEnabled}>
                    Suggest
                  </Button>
                </Popconfirm>
              </FormItem>
            </StyledGridCol>

            {/* Loc. 2 */}
            <StyledGridCol lg={5}>
              <SecureFormItem
                fieldName="storageLocation3Id"
                formItemProps={{
                  name: ["inventoryItems", 0, "storageLocation3Id"],
                  label: "Loc. 2",
                  rules: !fullSearch ? [{ required: true, message: "Loc. 2 is required" }] : [],
                }}
              >
                <Select
                  showSearch
                  options={getOptionsFromRefList("Inmate_Property_Storage_Location_2", false, false, true)}
                />
              </SecureFormItem>
            </StyledGridCol>

            {/* Property Type */}
            <StyledGridCol lg={5}>
              <SecureFormItem
                fieldName="inmatePropertyType"
                formItemProps={{
                  name: "inmatePropertyType",
                  label: "Property Type",
                  rules: !fullSearch ? [{ required: true, message: "Property Type is required" }] : [],
                }}
              >
                <Select showSearch options={getOptionsFromRefList("PropertyType")} />
              </SecureFormItem>
            </StyledGridCol>

            {/* Color */}
            <StyledGridCol lg={4}>
              <SecureFormItem
                fieldName="color"
                formItemProps={{
                  name: ["inventoryItems", 0, "propertyCommon", 0, "color"],
                  label: "Color",
                  rules: !fullSearch ? [{ required: true, message: "Color is required" }] : [],
                }}
              >
                <Select showSearch options={getOptionsFromRefList("PropertyColor")} />
              </SecureFormItem>
            </StyledGridCol>

            {/* Quantity */}
            <StyledGridCol lg={3}>
              <SecureFormItem
                fieldName="quantity"
                formItemProps={{
                  name: ["inventoryItems", 0, "propertyCommon", 0, "quantity"],
                  label: "Quantity",
                  rules: !fullSearch ? [{ required: true, message: "Quantity is required" }] : [],
                }}
              >
                <TextField
                  onKeyDown={(e) => {
                    if (
                      e.key.match(/[^0-9]/) &&
                      !["Backspace", "Tab", "ArrowLeft", "ArrowRight"].includes(e.key)
                    ) e.preventDefault();
                  }}
                />
              </SecureFormItem>
            </StyledGridCol>

            {/* Weapon? */}
            <StyledGridCol lg={4}>
              <SecureFormItem
                fieldName="weapon"
                formItemProps={{
                  name: ["inventoryItems", 0, "propertyCommon", 0, "weapon"],
                  label: "Weapon?",
                  labelPostion: "label-right",
                  layout: "horizontal",
                  paddingTop: "24px",
                  valuePropName: "checked",
                }}
              >
                <Checkbox />
              </SecureFormItem>
            </StyledGridCol>

            {/* Serial Number */}
            <StyledGridCol lg={6}>
              <SecureFormItem
                fieldName="serialNumber"
                formItemProps={{
                  name: ["inventoryItems", 0, "propertyWithSerialNumber", 0, "serialNumber"],
                  label: "Serial Number",
                }}
              >
                <TextField />
              </SecureFormItem>
            </StyledGridCol>

            {/* Description + Q-Add button */}
            <StyledGridCol lg={11}>
              <SecureFormItem
                fieldName="description"
                formItemProps={{
                  name: ["inventoryItems", 0, "propertyCommon", 0, "description"],
                  label: "Description",
                }}
              >
                {/* cloneElement injects disabled/className onto TextField only.
                    The Q-Add Button sits alongside and uses canAdd directly. */}
                <TextField />
              </SecureFormItem>
              <StyledFlex gap="10px" style={{ marginTop: "-8px" }}>
                <Button
                  onClick={onQuickAdd}
                  disabled={!bookingsFieldsEnabled || !canAdd}
                >
                  Q-Add
                </Button>
              </StyledFlex>
            </StyledGridCol>

          </StyledGridRow>
        </Form>
      </>
    );
  }
);

export default PropertyListQuickAddForm;